import { ReactNode, SetStateAction, useState } from "react";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import toast from "../../module/Toast/toast.tsx";
import { usePost, usePut } from "../Fetch/useFetch.tsx";
import { ErrorMessage } from "./Form.tsx";

const resolveReject = (
  onSubmit: any,
  setErrorMessage: (value: SetStateAction<null>) => void,
  successToastMessage = "L'élément a bien été enregistré",
  errorToastMessage = "L'élément n'a pas été enregistré",
  redirectFn?: any,
) => {
  return {
    onResolve: (result: string) => {
      setErrorMessage(null);
      toast.success(successToastMessage);
      onSubmit?.(result);
      redirectFn?.();
    },
    onReject: async (error: {
      text: () => SetStateAction<null> | PromiseLike<SetStateAction<null>>;
    }) => {
      setErrorMessage(await error.text());
      toast.error(errorToastMessage);
    },
  };
};

const useMyFormik = (
  submitUrl: string,
  isPost: boolean,
  onSubmit: any,
  successToastMessage: string | undefined,
  errorToastMessage: string | undefined,
  redirectUrl: string,
  isMultipartFormData: boolean,
) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const redirectFn = redirectUrl
    ? () => {
        navigate(redirectUrl);
      }
    : null;
  const postState = usePost(
    submitUrl,
    resolveReject(
      onSubmit,
      setErrorMessage,
      successToastMessage,
      errorToastMessage,
      redirectFn,
    ),
  );
  const putState = usePut(
    submitUrl,
    resolveReject(onSubmit, setErrorMessage),
    isMultipartFormData,
  );
  return { submitState: isPost ? postState : putState, errorMessage };
};

type MyFormikProps = {
  children?: ReactNode;
  initialStatus?: object;
  initialValues: object;
  prepareVariables: (...args: any[]) => any;
  validationSchema: object;
  submitUrl: string;
  isPost: boolean;
  redirectUrl: string;
  onSubmit: (...args: any[]) => any;
  successToastMessage?: string;
  errorToastMessage?: string;
  afficheLog?: boolean;
  classNameLog?: string;
  isMultipartFormData?: boolean;
};

type Log = {
  libelleLog: string;
};

const MyFormik = (props: MyFormikProps) => {
  const { submitState, errorMessage } = useMyFormik(
    props.submitUrl,
    props.isPost,
    props.onSubmit,
    props.successToastMessage,
    props.errorToastMessage,
    props.redirectUrl,
    props.isMultipartFormData ?? false,
  );

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.initialValues}
      initialStatus={props.initialStatus}
      validationSchema={props.validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const variables = props.prepareVariables
          ? props.prepareVariables(values)
          : values;
        try {
          await submitState.run(variables);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <>
        {props.afficheLog &&
          submitState.data &&
          submitState.data.length !== 0 && (
            <div className={props.classNameLog}>
              Logs :
              {submitState.data.map((e: Log, index: number) => (
                <div key={index}>{e.libelleLog}</div>
              ))}
            </div>
          )}
        <ErrorMessage error={errorMessage} />
        {props.children}
      </>
    </Formik>
  );
};

export default MyFormik;
